import { ref, type Ref } from "vue";
import { watchDebounced } from "@vueuse/core";
import { useUsersStore } from "@stores/users";

export interface Prospect {
  email: string;
  status: "existing" | "new";
  first_name: string;
  last_name: string;
  fullName?: string;
  accounts?: { id: number; name: string }[];
}

export interface UserSuggestion {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  accounts?: { id: number; name: string }[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const suggestionLabel = (user: UserSuggestion): string =>
  user.full_name?.trim() || [user.first_name, user.last_name].filter(Boolean).join(" ");

export const useProspectSearch = (prospects: Ref<Prospect[]>, add: (prospect: Prospect) => void) => {
  const usersStore = useUsersStore();

  const emailInput = ref("");
  const loading = ref(false);
  const searching = ref(false);
  const showSuggestions = ref(false);
  const suggestions = ref<UserSuggestion[]>([]);
  const errorMessage = ref("");

  const alreadyAdded = (email: string): boolean => prospects.value.some((p) => p.email === email.toLowerCase());

  watchDebounced(
    emailInput,
    async (value) => {
      const query = value.trim();
      errorMessage.value = "";
      if (query.length < 3) {
        suggestions.value = [];
        showSuggestions.value = false;
        return;
      }
      searching.value = true;
      showSuggestions.value = true;
      try {
        const results = (await usersStore.searchUsers(query)) as UserSuggestion[];
        suggestions.value = results.filter((user) => !alreadyAdded(user.email));
      } catch {
        suggestions.value = [];
      } finally {
        searching.value = false;
      }
    },
    { debounce: 250 },
  );

  const reset = (): void => {
    emailInput.value = "";
    suggestions.value = [];
    showSuggestions.value = false;
  };

  const selectSuggestion = (user: UserSuggestion, { keepSearching = false } = {}): void => {
    if (!alreadyAdded(user.email)) {
      add({
        email: user.email.toLowerCase(),
        status: "existing",
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        fullName: user.full_name,
        accounts: user.accounts ?? [],
      });
    }
    if (keepSearching) suggestions.value = suggestions.value.filter((suggestion) => suggestion.id !== user.id);
    else reset();
  };

  const addTypedEmail = async (): Promise<void> => {
    const email = emailInput.value.trim().toLowerCase();
    errorMessage.value = "";
    if (!email) return;

    if (!EMAIL_RE.test(email)) {
      errorMessage.value = "Invalid email address";
      return;
    }
    if (alreadyAdded(email)) {
      errorMessage.value = "This email is already in the list";
      return;
    }

    loading.value = true;
    try {
      const result = await usersStore.lookupByEmail(email);
      add(
        result?.found
          ? {
              email,
              status: "existing",
              first_name: result.first_name ?? "",
              last_name: result.last_name ?? "",
              fullName: result.full_name,
              accounts: result.accounts ?? [],
            }
          : { email, status: "new", first_name: "", last_name: "" },
      );
      reset();
    } catch {
      errorMessage.value = "Could not check this email, try again";
    } finally {
      loading.value = false;
    }
  };

  return { emailInput, loading, searching, showSuggestions, suggestions, errorMessage, selectSuggestion, addTypedEmail };
};
